import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UserRoles } from '@authentication/user-roles';
import { OrderCardListComponent } from '@orders/order-card-list/order-card-list.component';
import { OrderComponent } from '@orders/order/order.component';
import { HastusOrderComponent } from '@orders/order/hastus-order/hastus-order.component';
import { OrderConfirmationComponent } from '@orders/order/order-confirmation/order-confirmation.component';
import { SubcontractorsComponent } from '@orders/order/subcontractors/subcontractors.component';
import { AvailabilityInquiryComponent } from '@orders/subcontractor/availability-inquiry/availability-inquiry.component';
import { GooglemapComponent } from './googlemap/googlemap.component';
import { RouteMapComponent } from '@orders/order/route-map/route-map.component';
import { SubcontractorOrderComponent } from '@orders/subcontractor/subcontractor-order/subcontractor-order.component';
import { OrderLogComponent } from '@orders/order/order-log/order-log.component';
import { SubcontractorRouteMapComponent } from '@orders/subcontractor/subcontractor-order/subcontractor-route-map/subcontractor-route-map.component';
import { ManageOperatorsComponent } from './admin/manage-operators/manage-operators.component';
import { OutcomeListComponent } from './billing/outcome-list/outcome-list.component';
import { OutcomeComponent } from './billing/outcome/outcome.component';
import { UnboundorderComponent } from '@orders/unboundorder/unboundorder.component';
import { UnboundOrderConfirmationComponent } from '@orders/unboundorder/unbound-order-confirmation/unbound-order-confirmation.component';
import { UnboundOrderListComponent } from '@orders/unbound-order-list/unbound-order-list.component';
import { UnboundOrderAssignmentComponent } from '@orders/unboundorder/unbound-order-assignment/unbound-order-assignment.component';
import { UnboundOrderCompletionComponent } from '@orders/unboundorder/unbound-order-completion/unbound-order-completion.component';
import { UnboundOrderLogComponent } from '@orders/unboundorder/unbound-order-log/unbound-order-log.component';
import { VersionComponent } from './admin/version/version.component';
import { CreateReportComponent } from './billing/reports/create-report/create-report.component';
import { ImportOrderComponent } from './admin/import-order/import-order.component';
import { ConnectRoutevariantComponent } from './admin/connect-routevariant/connect-routevariant.component';
import { ImportTimetableComponent } from './admin/import-timetable/import-timetable.component';
import { NotificationsComponent } from '@common/components/notifications/notifications.component';
import { PositionHistoryMapComponent } from '@orders/order/position-history-map/position-history-map.component';
import { MissingVehicleStopEventsComponent } from './admin/missing-vehicle-stop-events/missing-vehicle-stop-events.component';
import { SubcontractorComponent } from '@orders/subcontractor/subcontractor/subcontractor.component';
import { SubcontractorHistoryComponent } from '@orders/subcontractor/subcontractor-history/subcontractor-history.component';
import { MsalGuard } from '@azure/msal-angular';
import { LoginFailedComponent } from './login-failed/login-failed.component';
import { ClearComponent } from './clear/clear.component';
import { ProductOrderComponent } from '@orders/order/new-order/product-order.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { StartPageComponent } from './start-page/start-page.component';
import { startGuardFn } from '@common/utils/start-guard-fn';
import { roleGuardFn } from '@common/utils/role-guard-fn';

// OBS det verkar vara lite lurigt med flera canActivate-guards. Se
// https://stackoverflow.com/questions/40589878/multiple-canactivate-guards-all-run-when-first-fails
// det verkar som om en enda canActivate som returnerar Observable<boolean> verkar funka bäst.

const orderRoles = [
  UserRoles.NobinaLeveransansvarig,
  UserRoles.NobinaTrafikledare,
  UserRoles.OperatorTrafikledare,
  UserRoles.OperatorInformator,
];

const subcontractorRoles = [UserRoles.UETrafikledare];

const historyRoles = [
  UserRoles.NobinaTrafikledare,
  UserRoles.NobinaLeveransansvarig,
  UserRoles.OperatorTrafikledare,
  UserRoles.OperatorInformator,
];

const settingsRoles = [UserRoles.NobinaLeveransansvarig, UserRoles.NobinaTrafikledare];

const billingOutcomesRoles = [
  UserRoles.NobinaLeveransansvarig,
  UserRoles.NobinaTrafikledare,
  UserRoles.UETrafikledare,
];

const billingReportsRoles = [
  UserRoles.NobinaTrafikledare,
  UserRoles.NobinaLeveransansvarig,
  UserRoles.OperatorTrafikledare,
  UserRoles.OperatorInformator,
  UserRoles.UETrafikledare,
];

const routes: Routes = [
  {
    path: '',
    component: StartPageComponent,
    canActivate: [startGuardFn],
  },
  { path: 'clear', component: ClearComponent },
  {
    path: 'order/:id',
    component: OrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order/planned-order/:id',
    component: HastusOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order/product-order/:id',
    component: ProductOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order',
    component: OrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order-confirmation/:id',
    component: OrderConfirmationComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order/:id/subcontractors',
    component: SubcontractorsComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order/:id/route-map',
    component: RouteMapComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order/:id/trip/:tripid/position-history-map',
    component: PositionHistoryMapComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'order/:id/log',
    component: OrderLogComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'subcontractor/:subcontractorId/availability-inquiry/:id',
    component: AvailabilityInquiryComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  {
    path: 'subcontractor/:subcontractorId/subcontractor-orders/:id',
    component: SubcontractorOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  {
    path: 'subcontractor/:subcontractorId/subcontractor-orders/:id/subcontractor-route-map',
    component: SubcontractorRouteMapComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  {
    path: 'subcontractor/:subcontractorId/assignments/:id',
    component: AvailabilityInquiryComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  // History related routes, usually reuses same component as original route
  {
    path: 'history/unboundorder',
    component: UnboundOrderListComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/unboundorder/:id',
    component: UnboundorderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/unboundorder/:id/assignment',
    component: UnboundOrderAssignmentComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/unboundorder/:id/completion',
    component: UnboundOrderCompletionComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/unboundorder/:id/log',
    component: UnboundOrderLogComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/order/:id/trip/:tripid/position-history-map',
    component: PositionHistoryMapComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history',
    component: OrderCardListComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/:id',
    component: OrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/planned-order/:id',
    component: HastusOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/product-order/:id',
    component: ProductOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/:id/subcontractors',
    component: SubcontractorsComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/:id/route-map',
    component: RouteMapComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'history/:id/log',
    component: OrderLogComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: historyRoles },
  },
  {
    path: 'subcontractor/:subcontractorId/history',
    component: SubcontractorHistoryComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  {
    path: 'subcontractor/:subcontractorId/history/:id',
    component: SubcontractorOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  {
    path: 'subcontractor/:subcontractorId/history/:id/subcontractor-route-map',
    component: SubcontractorRouteMapComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  {
    path: 'subcontractor/:subcontractorId/assignment-history/:id',
    component: AvailabilityInquiryComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  // Billing
  {
    path: 'billing/outcomes',
    component: OutcomeListComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: billingOutcomesRoles, requireBillingEnabled: true },
  },
  {
    path: 'billing/outcomes/:id',
    component: OutcomeComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: billingOutcomesRoles, requireBillingEnabled: true },
  },
  {
    path: 'billing/reports/create-report',
    component: CreateReportComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: billingReportsRoles },
  },
  // Unbound order (Friorder)
  {
    path: 'unboundorder',
    component: UnboundorderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'unbound-order-confirmation/:id',
    component: UnboundOrderConfirmationComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'unboundorder/:id',
    component: UnboundorderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'unboundorder/:id/assignment',
    component: UnboundOrderAssignmentComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'unboundorder/:id/completion',
    component: UnboundOrderCompletionComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'unboundorder/:id/log',
    component: UnboundOrderLogComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  // Special routes
  { path: 'notifications', component: NotificationsComponent, canActivate: [MsalGuard] },
  {
    path: 'usersettings',
    component: ManageOperatorsComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: orderRoles },
  },
  {
    path: 'settings/import-order',
    component: ImportOrderComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: settingsRoles },
  },
  {
    path: 'settings/import-timetable',
    component: ImportTimetableComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: settingsRoles },
  },
  {
    path: 'settings/connect-routevariant',
    component: ConnectRoutevariantComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: settingsRoles },
  },
  {
    path: 'settings/missingVehicleStopEvents',
    component: MissingVehicleStopEventsComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: settingsRoles },
  },
  { path: 'version', component: VersionComponent },
  { path: 'googlemap', component: GooglemapComponent },
  {
    path: 'subcontractor/:subcontractorId',
    component: SubcontractorComponent,
    canActivate: [MsalGuard, roleGuardFn],
    data: { roles: subcontractorRoles, requireMatchingGarageId: true },
  },
  { path: 'page-not-found', component: PageNotFoundComponent },
  { path: 'login-failed', component: LoginFailedComponent },
  { path: '**', redirectTo: '/page-not-found' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
